// Escuta ativa GLOBAL da Clara — motor mínimo de voz no modo sempre-aberto
// (VAD + nome no texto do whisper), desacoplado do ClaraLab: mic → AudioWorklet
// (PCM16 mono 16 kHz) → WS /clara-ws/ws (nginx → serviço host clara-wake:11439)
// → respostas voltam como WAV binário e tocam em fila. Sem UI textual: o único
// consumidor visual é o botão do rodapé da CrmSidebar (liga/desliga).
// O ClaraLab (aba Tecnologia) segue com o motor próprio e mais completo; ao
// iniciar a escuta lá, ele chama o stop() daqui pra não haver dois ouvintes
// respondendo em dobro.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

// Worklet inline: reamostra do sampleRate do contexto pra 16 kHz e emite
// Int16Array em blocos de 1280 amostras (80 ms). Compartilhado com o ClaraLab.
export const WORKLET_SRC = `
class Pcm16Downsampler extends AudioWorkletProcessor {
  constructor() {
    super()
    this.ratio = sampleRate / 16000
    this.readPos = 0
    this.queue = new Float32Array(0)
    this.out = new Int16Array(1280)
    this.outLen = 0
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (!ch) return true
    const merged = new Float32Array(this.queue.length + ch.length)
    merged.set(this.queue); merged.set(ch, this.queue.length)
    let pos = this.readPos
    while (pos + 1 < merged.length) {
      const i = Math.floor(pos)
      const frac = pos - i
      const s = merged[i] * (1 - frac) + merged[i + 1] * frac
      this.out[this.outLen++] = Math.max(-32768, Math.min(32767, Math.round(s * 32767)))
      if (this.outLen === 1280) {
        const copy = this.out.slice()
        this.port.postMessage(copy.buffer, [copy.buffer])
        this.outLen = 0
      }
      pos += this.ratio
    }
    const consumed = Math.floor(pos)
    this.queue = merged.slice(consumed)
    this.readPos = pos - consumed
    return true
  }
}
registerProcessor('pcm16-downsampler', Pcm16Downsampler)
`

export function beep(freq: number) {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0.12, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
    osc.connect(gain).connect(ctx.destination)
    osc.start(); osc.stop(ctx.currentTime + 0.25)
    osc.onended = () => ctx.close()
  } catch { /* sem áudio de saída, segue */ }
}

// Estágios: off/connecting locais; listening/working/speaking seguem o turno
// no servidor (status/no_speech/error) e o playback local — alimentam o orbe
// animado do rodapé da sidebar (ClaraOrb), sem nenhuma UI textual.
// 'declined' (transitório, ~3s): comando descartado/não entendido/julgado
// ruído — alerta visual antes de voltar ao 'listening' (pedido 2026-07-18:
// nunca voltar do amarelo pro verde em silêncio).
export type ClaraVoiceStage = 'off' | 'connecting' | 'listening' | 'working' | 'speaking' | 'declined'

// Timeline do turno — etapas amigáveis exibidas pelo painel-revelação da
// sidebar (ClaraTurnPanel). Nasce no evento `transcript` (que o servidor só
// emite DEPOIS do guardrail aprovar = comando identificado; a escuta em si
// não gera timeline) e acompanha o turno até o fim da fala.
export type ClaraTurnStepStatus = 'doing' | 'done' | 'error'
export interface ClaraTurnStep {
  id: number
  title: string
  detail?: string
  status: ClaraTurnStepStatus
  // Sem clamp de linhas no detalhe (usado pela "Resposta pronta", que digita
  // em sincronia com a fala e precisa aparecer inteira).
  full?: boolean
}

interface ClaraVoiceCtx {
  stage: ClaraVoiceStage
  toggle: () => void
  stop: () => void
  turnOpen: boolean
  turnSteps: ClaraTurnStep[]
  dismissTurn: () => void
}

const Ctx = createContext<ClaraVoiceCtx>({
  stage: 'off',
  toggle: () => { /* sem provider */ },
  stop: () => { /* sem provider */ },
  turnOpen: false,
  turnSteps: [],
  dismissTurn: () => { /* sem provider */ },
})

export const useClaraVoice = () => useContext(Ctx)

export function ClaraVoiceProvider({ children }: { children: ReactNode }) {
  const [stage, setStage] = useState<ClaraVoiceStage>('off')
  const [turnSteps, setTurnSteps] = useState<ClaraTurnStep[]>([])
  const [turnOpen, setTurnOpen] = useState(false)
  const stepIdRef = useRef(0)
  // Fase do turno visível: 'wake' entre o chamado ("Clara") e o comando,
  // 'thinking' entre o comando e a resposta, 'replied' enquanto a fala toca —
  // o dreno do playback (pumpAudio) usa isso pra saber que o áudio que acabou
  // era a RESPOSTA (fillers também drenam a fila).
  const turnPhaseRef = useRef<'idle' | 'wake' | 'thinking' | 'replied'>('idle')
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Typewriter da "Resposta pronta": o servidor manda `speak_part` (texto do
  // pedaço) imediatamente antes do WAV correspondente — a fila pareia texto e
  // áudio pela ordem do WS, e cada pedaço é digitado no detalhe da etapa ao
  // longo da duração real do áudio decodificado (sincronia com a fala).
  const partQueueRef = useRef<string[]>([])
  const twTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const twDoneRef = useRef('')      // pedaços já digitados por completo
  const twCurrentRef = useRef('')   // pedaço em digitação (pra flush)
  const replyStepIdRef = useRef<number | null>(null)
  const fullReplyRef = useRef('')
  const declinedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const activeRef = useRef(false)
  // Fila de playback do TTS: um WAV por vez; playing/idle avisam o servidor
  // pra silenciar a captura enquanto a Clara fala (senão ela ouve a si mesma).
  const audioQueueRef = useRef<ArrayBuffer[]>([])
  const playingRef = useRef(false)
  const speakEndedRef = useRef(true)
  // Geração da sessão: callbacks pendentes de um start antigo (timeout de
  // conexão, onclose do WS fechado, catch) só podem derrubar a SUA sessão —
  // sem isso, ligar→desligar→ligar rápido deixava um teardown atrasado
  // matando a escuta nova em silêncio.
  const genRef = useRef(0)

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null }
  }, [])

  const scheduleClose = useCallback((ms: number) => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => setTurnOpen(false), ms)
  }, [cancelClose])

  const pushStep = useCallback((title: string, detail?: string, status: ClaraTurnStepStatus = 'doing', full?: boolean) => {
    // id fora do updater: updater precisa ser puro (StrictMode roda em dobro)
    const id = ++stepIdRef.current
    setTurnSteps(s => [...s, { id, title, detail, status, full }])
    return id
  }, [])

  const setReplyDetail = useCallback((text: string) => {
    const id = replyStepIdRef.current
    if (id == null) return
    setTurnSteps(s => s.map(st => (st.id === id ? { ...st, detail: text } : st)))
  }, [])

  // Zera o typewriter sem escrever nada (turno novo / desligamento).
  const resetTypewriter = useCallback(() => {
    if (twTimerRef.current) { clearInterval(twTimerRef.current); twTimerRef.current = null }
    partQueueRef.current = []
    twDoneRef.current = ''
    twCurrentRef.current = ''
    replyStepIdRef.current = null
    fullReplyRef.current = ''
  }, [])

  // Fim do turno: despeja o texto COMPLETO da resposta (cobre arredondamento
  // do ritmo, TTS que falhou no meio e o caso sem áudio nenhum).
  const finishReplyTypewriter = useCallback(() => {
    if (twTimerRef.current) { clearInterval(twTimerRef.current); twTimerRef.current = null }
    partQueueRef.current = []
    twDoneRef.current = ''
    twCurrentRef.current = ''
    if (fullReplyRef.current) setReplyDetail(fullReplyRef.current)
  }, [setReplyDetail])

  // Chamado quando um WAV da RESPOSTA começa a tocar: pega o texto pareado da
  // fila e digita ao longo da duração do áudio. Pedaço anterior inacabado é
  // despejado inteiro antes (o áudio dele já acabou).
  const startPartTypewriter = useCallback((durationSec: number) => {
    const part = partQueueRef.current.shift()
    if (part == null || replyStepIdRef.current == null) return
    if (twTimerRef.current) { clearInterval(twTimerRef.current); twTimerRef.current = null }
    if (twCurrentRef.current) {
      twDoneRef.current = (twDoneRef.current ? `${twDoneRef.current} ` : '') + twCurrentRef.current
      twCurrentRef.current = ''
    }
    twCurrentRef.current = part
    const base = twDoneRef.current ? `${twDoneRef.current} ` : ''
    const ticks = Math.max(1, Math.floor((durationSec * 1000) / 50))
    const perTick = Math.max(1, Math.ceil(part.length / ticks))
    let shown = 0
    twTimerRef.current = setInterval(() => {
      shown = Math.min(part.length, shown + perTick)
      setReplyDetail(base + part.slice(0, shown))
      if (shown >= part.length && twTimerRef.current) {
        clearInterval(twTimerRef.current)
        twTimerRef.current = null
        twDoneRef.current = base + part
        twCurrentRef.current = ''
      }
    }, 50)
  }, [setReplyDetail])

  // Fecha a(s) etapa(s) em andamento; em erro, a mensagem entra como detalhe.
  const settleDoing = useCallback((status: 'done' | 'error', detail?: string) => {
    setTurnSteps(s => s.map(st => (st.status === 'doing' ? { ...st, status, detail: detail ?? st.detail } : st)))
  }, [])

  const dismissTurn = useCallback(() => {
    cancelClose()
    setTurnOpen(false)
  }, [cancelClose])

  // Alerta de comando recusado: lima por ~3s, depois volta ao verde sozinho
  // (a menos que outro estágio tenha assumido nesse meio-tempo).
  const declineToListening = useCallback(() => {
    setStage('declined')
    if (declinedTimerRef.current) clearTimeout(declinedTimerRef.current)
    declinedTimerRef.current = setTimeout(() => {
      declinedTimerRef.current = null
      setStage(s => (s === 'declined' ? 'listening' : s))
    }, 3200)
  }, [])

  const stop = useCallback(() => {
    genRef.current++
    activeRef.current = false
    wsRef.current?.close()
    wsRef.current = null
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    ctxRef.current?.close().catch(() => { /* já fechado */ })
    ctxRef.current = null
    audioQueueRef.current = []
    playingRef.current = false
    speakEndedRef.current = true
    turnPhaseRef.current = 'idle'
    cancelClose()
    resetTypewriter()
    if (declinedTimerRef.current) { clearTimeout(declinedTimerRef.current); declinedTimerRef.current = null }
    setTurnOpen(false)
    setTurnSteps([])
    setStage('off')
  }, [cancelClose, resetTypewriter])

  const pumpAudio = useCallback(() => {
    const ctx = ctxRef.current
    const ws = wsRef.current
    if (!ctx || playingRef.current) return
    const buf = audioQueueRef.current.shift()
    if (!buf) {
      if (speakEndedRef.current) {
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'idle' }))
        // Fila drenada DEPOIS da resposta = fim do turno visível (fillers
        // também drenam, por isso o gate na fase). Fecha a etapa "Falando a
        // resposta" e agenda o recolhimento do painel.
        if (turnPhaseRef.current === 'replied') {
          turnPhaseRef.current = 'idle'
          finishReplyTypewriter()
          settleDoing('done')
          scheduleClose(8000)
        }
        // Missão concluída (fase idle) → verde; dreno de FILLER (ainda
        // pensando) → volta pro amarelo, nunca pro verde no meio da missão
        // (regra 2026-07-18).
        setStage(s => (s === 'speaking' ? (turnPhaseRef.current === 'idle' ? 'listening' : 'working') : s))
      }
      return
    }
    playingRef.current = true
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'playing' }))
    setStage('speaking')
    ctx.decodeAudioData(buf.slice(0), decoded => {
      try {
        const src = ctx.createBufferSource()
        src.buffer = decoded
        src.connect(ctx.destination)
        src.onended = () => { playingRef.current = false; pumpAudio() }
        src.start()
        // Áudio da resposta começou a tocar → digita o pedaço pareado dele
        // no ritmo da duração real (fillers tocam na fase 'thinking' e não
        // enfileiram speak_part, então não entram aqui).
        if (turnPhaseRef.current === 'replied') startPartTypewriter(decoded.duration)
      } catch { playingRef.current = false /* ctx fechado por um stop() no meio do decode */ }
    }, () => { playingRef.current = false; pumpAudio() })
  }, [settleDoing, scheduleClose, finishReplyTypewriter, startPartTypewriter])

  const start = useCallback(async () => {
    const gen = ++genRef.current
    setStage('connecting')
    activeRef.current = true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      if (gen !== genRef.current) { stream.getTracks().forEach(t => t.stop()); return }
      streamRef.current = stream

      const token = localStorage.getItem('auth-token') || ''
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      const ws = new WebSocket(`${proto}://${window.location.host}/clara-ws/ws?token=${encodeURIComponent(token)}`)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onmessage = ev => {
        if (typeof ev.data !== 'string') {
          audioQueueRef.current.push(ev.data as ArrayBuffer)
          pumpAudio()
          return
        }
        const msg = JSON.parse(ev.data)
        switch (msg.type) {
          case 'ready':
            setStage('listening')
            break
          case 'wake':
            beep(880)
            // Orbe já troca pra animação de "trabalhando" no instante do
            // chamado (pedido 2026-07-18) — antes, só quando o servidor
            // entrava em transcribing/thinking. Se o turno morrer sem fala
            // (no_speech/guardrail), os handlers abaixo voltam a 'listening'.
            setStage('working')
            // Timeline abre já no chamado (pedido 2026-07-18): as etapas de
            // comando/resposta continuam a partir destas quando o turno vinga;
            // se morrer (ruído/nada entendido), os handlers de morte encerram.
            cancelClose()
            resetTypewriter()
            turnPhaseRef.current = 'wake'
            setTurnSteps([])
            pushStep('Chamado reconhecido', undefined, 'done')
            pushStep('Ouvindo o comando')
            setTurnOpen(true)
            break
          case 'status':
            // transcribing/thinking → processando. `listening` só chega nos
            // abortos precoces do servidor (whisper vazio / guardrail julgou
            // ruído) — vira o alerta 'declined' em vez de verde silencioso;
            // com missão visível em andamento (thinking/replied), jamais
            // regride pro verde.
            if (msg.stage === 'listening') {
              if (turnPhaseRef.current === 'wake') {
                // chamado aberto mas o comando foi descartado — encerra a
                // timeline com o motivo
                turnPhaseRef.current = 'idle'
                settleDoing('error', 'Descartado: ruído ou conversa de fundo')
                scheduleClose(6000)
                declineToListening()
              } else if (turnPhaseRef.current === 'idle') {
                declineToListening()
              } else {
                setStage('working')
              }
            } else {
              setStage('working')
            }
            break
          case 'no_speech':
            // capturou áudio mas não entendeu fala nenhuma — alerta visual
            declineToListening()
            if (turnPhaseRef.current !== 'idle') {
              turnPhaseRef.current = 'idle'
              settleDoing('error', 'Não entendi nenhuma fala')
              scheduleClose(6000)
            }
            break
          case 'stopped':
          case 'error':
            // stop manual volta direto ao verde (foi comando do usuário);
            // erro ganha o alerta 'declined' antes de voltar a escutar
            if (msg.type === 'error') declineToListening()
            else setStage('listening')
            // Se um comando já tinha sido identificado, encerra a timeline:
            // erro marca a etapa em andamento em vermelho com a mensagem;
            // stop manual registra a interrupção. (no_speech vem antes do
            // transcript — a fase 'idle' o ignora.)
            if (turnPhaseRef.current !== 'idle') {
              turnPhaseRef.current = 'idle'
              // Se já havia resposta digitando, despeja o texto completo antes
              // de encerrar — o usuário lê o que a fala não chegou a cobrir.
              finishReplyTypewriter()
              if (msg.type === 'error') {
                settleDoing('error', msg.message)
                scheduleClose(12000)
              } else {
                settleDoing('done')
                if (msg.type === 'stopped') pushStep('Interrompido por você', undefined, 'done')
                scheduleClose(6000)
              }
            }
            break
          case 'speak_start':
            speakEndedRef.current = false
            break
          case 'speak_end':
            speakEndedRef.current = true
            pumpAudio()
            break
          // Timeline do painel da sidebar — só nasce no comando aprovado
          // (transcript), nunca na escuta.
          case 'transcript':
            cancelClose()
            if (turnPhaseRef.current === 'wake') {
              // continua a timeline aberta no chamado: fecha "Ouvindo o comando"
              settleDoing('done')
            } else {
              // defensivo (transcript sem wake antes): começa do zero
              resetTypewriter()
              setTurnSteps([])
            }
            turnPhaseRef.current = 'thinking'
            pushStep('Comando identificado', msg.text, 'done')
            pushStep('Pensando na resposta')
            setTurnOpen(true)
            break
          case 'filler':
            // Frase de espera falada enquanto pensa — vira detalhe da etapa
            // "Pensando", não uma etapa nova.
            if (turnPhaseRef.current === 'thinking') {
              setTurnSteps(s => s.map(st => (st.status === 'doing' ? { ...st, detail: `“${msg.text}”` } : st)))
            }
            break
          case 'reply':
            if (turnPhaseRef.current === 'thinking') {
              turnPhaseRef.current = 'replied'
              settleDoing('done')
              // Detalhe nasce vazio e é digitado em sincronia com a fala
              // (speak_part + duração do WAV); o texto completo é garantido
              // no fim do turno pelo finishReplyTypewriter.
              fullReplyRef.current = msg.text
              replyStepIdRef.current = pushStep('Resposta pronta', '', 'done', true)
              pushStep('Falando a resposta')
            }
            break
          case 'speak_part':
            if (turnPhaseRef.current === 'replied') partQueueRef.current.push(msg.text)
            break
          // demais eventos (score) não têm UI neste modo; a fala chega como
          // binário acima e o estágio 'speaking' é setado pelo pumpAudio.
        }
      }
      ws.onclose = () => { if (gen === genRef.current) stop() }
      ws.onerror = () => { /* onclose cobre */ }

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve()
        const t = setTimeout(() => reject(new Error('timeout conectando no serviço de escuta')), 8000)
        ws.addEventListener('open', () => clearTimeout(t), { once: true })
      })
      if (gen !== genRef.current) return

      const ctx = new AudioContext()
      ctxRef.current = ctx
      const workletUrl = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }))
      await ctx.audioWorklet.addModule(workletUrl)
      URL.revokeObjectURL(workletUrl)
      const src = ctx.createMediaStreamSource(stream)
      const node = new AudioWorkletNode(ctx, 'pcm16-downsampler')
      node.port.onmessage = e => { if (ws.readyState === WebSocket.OPEN) ws.send(e.data) }
      src.connect(node)
      // worklet não precisa tocar nada; conectar a um gain mudo mantém o grafo vivo
      const mute = ctx.createGain(); mute.gain.value = 0
      node.connect(mute).connect(ctx.destination)
    } catch {
      // mic negado, WS fora, timeout — volta pro desligado em silêncio;
      // se outra sessão já assumiu (gen mudou), não derruba a dela
      if (gen === genRef.current) stop()
    }
  }, [pumpAudio, stop, cancelClose, pushStep, settleDoing, scheduleClose, resetTypewriter, finishReplyTypewriter, declineToListening])

  const toggle = useCallback(() => {
    if (activeRef.current) stop()
    else void start()
  }, [start, stop])

  useEffect(() => () => stop(), [stop])

  return <Ctx.Provider value={{ stage, toggle, stop, turnOpen, turnSteps, dismissTurn }}>{children}</Ctx.Provider>
}
